<template>
  <hm-col :id="$attrs['id']" :cols="cols">
    <v-date-picker ref="picker"
                   v-model="selectedDate"
                   :locale="$locale"
                   :min-date="minimumDate"
                   :max-date="maximumDate"
                   :popover-content-offset="0"
                   :content-style="{ highlight: { height: '2.88rem', borderRadius: '2.88rem' } }"
                   :highlight="{ height: '2.88rem', borderRadius: '2.88rem' }"
                   :popover="{
                     visibility: disabledCombined === true ? 'hidden' : 'focus',
                     placement: 'bottom',
                     positionFixed: fixed
                   }"
                   color="teal"
                   mode="single">
      <hm-input ref="input"
                slot-scope="props"
                v-model="formattedDate"
                :lazy="false"
                :class="$attrs['class']"
                :label="label"
                :help="help"
                :has-focus="hasFocus"
                :is-empty="isValueEmpty"
                :name="name"
                :disabled="disabledCombined"
                :validate="validate"
                :parent-errors="parentErrors"
                :readonly="$store.getters['layout/activeBreakpoint'] === 'mobile'"
                input-type="HmInputDate"
                @input="updateDateFromFormatted"
                @focus="onFocus"
                @blur="onBlur">
        <template slot="icon-inline-left" slot-scope="slot">
          <slot name="icon-inline-left"></slot>
        </template>
        <template slot="icon-inline-right" slot-scope="slot">
          <input-icon-button v-if="required === false && !isNil(selectedDate) && !disabledCombined" :border="false" icon="close-text" @click="internalValue = null" />
          <input-icon-button :border="true" :disabled="disabledCombined" icon="cal" style="pointer-events: none;"/>
        </template>
      </hm-input>
    </v-date-picker>
    <resize-sensor @resize="recalculatePosition"></resize-sensor>
  </hm-col>
</template>

<script>

import { isNil, isEmpty, trim, isArray } from 'lodash';
import HmInputMixin from '@/mixins/HmInputMixin';
import ResizeSensor from 'vue-resize-sensor';
import HotelmeisterMixin from '../../../mixins/HotelmeisterMixin';
import TimerMixin from '../../../mixins/TimerMixin';

export default {
  name: 'HmInputDate',

  components: {
    'resize-sensor': ResizeSensor,
  },
  mixins: [HmInputMixin, HotelmeisterMixin, TimerMixin],

  props: {
    minDate: {
      required: false,
      type: String,
      default: '18000101',
    },
    maxDate: {
      required: false,
      type: String,
      default: null,
    },
    minDateInclusive: {
      required: false,
      type: Boolean,
      default: false,
    },
    maxDays: {
      required: false,
      type: Number,
      default: 120,
    },
    required: {
      required: false,
      type: Boolean,
      default: true,
    },
    fixed: {
      required: false,
      type: Boolean,
      default: true,
    },
    autoAdjust: {
      required: false,
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      selectedDate: null,
      created: false,
      formattedDate: null,
      minimumDate: null,
      calendarPositionParams: {
        offsetLeft: 0,
        width: 0,
        calendarWidth: 260,
      },
      shortcutAdd1Month: () => this.handleShortcut(1, 'month'),
      shortcutSubtract1Month: () => this.handleShortcut(-1, 'month'),
      shortcutAdd1Year: () => this.handleShortcut(1, 'year'),
      shortcutSubtract1Year: () => this.handleShortcut(-1, 'year'),
    };
  },

  computed: {
    maxHotelDate: function () {
      return this.$moment().add(this.hotelSettings.details.futureDates, 'days').format('YYYYMMDD');
    },
    maximumDate: function () {
      if (!isEmpty(this.maxDate)) return this.$moment(this.maxDate, 'YYYYMMDD').toDate();

      if (isNil(this.minimumDate) || this.maxDays <= 0 || this.minDate === '18000101') return this.$moment(this.maxHotelDate, 'YYYYMMDD').toDate();

      return this.$moment(this.minimumDate).add(this.maxDays, 'days').toDate();
    },
    calendarAlignment: function () {
      return this.calendarPositionParams.offsetLeft + this.calendarPositionParams.width > this.calendarPositionParams.calendarWidth
        ? 'right'
        : 'left';
    },
  },

  watch: {
    minDate: function () {
      const previousMinimumDate = this.$moment(this.minimumDate);
      this.minimumDate = isNil(this.minDate) ? null : this.$moment(this.minDate, 'YYYYMMDD').add(this.minDateInclusive ? 0 : 1, 'days').toDate();

      let diffDays = 0;

      if (!isNil(previousMinimumDate) && !isNil(this.internalValue)) {
        diffDays = Math.round(Math.abs(this.$moment.duration(previousMinimumDate.diff(this.$moment(this.internalValue, 'YYYYMMDD'))).asDays()));
      }

      if (this.autoAdjust) {
        this.internalValue = this.$moment(this.minimumDate).add(diffDays, 'days').format('YYYYMMDD');
      } else if (!this.$moment(this.minimumDate).isBefore(this.$moment(this.internalValue, 'YYYYMMDD'))) {
        this.internalValue = this.$moment(this.minimumDate).add(this.minDateInclusive ? 0 : 1, 'days').format('YYYYMMDD');
      }
    },
    internalValue: function (newValue) {
      const newDate = isNil(newValue) ? null : this.$moment.tz(newValue, 'YYYYMMDD', Intl.DateTimeFormat().resolvedOptions().timeZone);
      this.selectedDate = isNil(newDate) ? null : newDate.toDate();
      if (!this.hasFocus) {
        // can't listen for focus only, because then shortcut dates are not accepted, because they get overriden by the formatted date
        this.formattedDate = isNil(newDate) ? null : newDate.format(this.$moment.localeData().longDateFormat('L'));
      }

      if (!isNil(this.selectedDate)) {
        this.$nextTick(() => {
          this.$refs.picker.adjustPageRange();
        });
      }
    },
    selectedDate: function (newVal, oldVal) {
      if (newVal === oldVal) return;
      if (this.created && (!this.required || !isNil(newVal))) {
        const newDate = isNil(newVal) ? null : this.$moment.tz(newVal, Intl.DateTimeFormat().resolvedOptions().timeZone);
        if (this.required === true && isNil(newVal) && !isNil(oldVal)) {
          this.internalValue = newDate.format('YYYYMMDD');
        } else {
          this.internalValue = isNil(newVal) ? null : newDate.format('YYYYMMDD');
        }
      }
    },
    '$i18n.locale': function () {
      this.formattedDate = isNil(this.selectedDate) ? null : this.$moment(this.selectedDate, 'YYYYMMDD').format(this.$moment.localeData().longDateFormat('L'));
    },
  },

  created: function () {
    this.selectedDate = isNil(this.internalValue) ? null : this.$moment(this.internalValue, 'YYYYMMDD').toDate();
    this.formattedDate = isNil(this.internalValue) ? null : this.$moment(this.internalValue, 'YYYYMMDD').format(this.$moment.localeData().longDateFormat('L'));
    this.created = true;

    this.minimumDate = isNil(this.minDate) ? null : this.$moment(this.minDate, 'YYYYMMDD').add(this.minDateInclusive ? 0 : 1, 'days').toDate();

    this.$serviceBus.$on('shortcut.pageRight', this.shortcutAdd1Month);
    this.$serviceBus.$on('shortcut.pageLeft', this.shortcutSubtract1Month);
    this.$serviceBus.$on('shortcut.pageUp', this.shortcutAdd1Year);
    this.$serviceBus.$on('shortcut.pageDown', this.shortcutSubtract1Year);
    this.$serviceBus.$on('shortcut.enter', this.handleEnter);
    this.$serviceBus.$on('shortcut.tab', this.handleEnter);
  },

  beforeDestroy: function () {
    this.$serviceBus.$off('shortcut.pageRight', this.shortcutAdd1Month);
    this.$serviceBus.$off('shortcut.pageLeft', this.shortcutSubtract1Month);
    this.$serviceBus.$off('shortcut.pageUp', this.shortcutAdd1Year);
    this.$serviceBus.$off('shortcut.pageDown', this.shortcutSubtract1Year);
    this.$serviceBus.$off('shortcut.enter', this.handleEnter);
    this.$serviceBus.$off('shortcut.tab', this.handleEnter);
  },

  mounted: function () {
    // this.recalculatePosition();
  },

  methods: {
    handleEnter: function () {
      if (this.hasFocus && this.$el) {
        this.$el.querySelector('.vc-popover-content').querySelectorAll('*[tabindex]').forEach((it) => { it.tabIndex = -1; });
        if (!isNil(this.$refs) && !isNil(this.$refs.input)) {
          this.$refs.input.blur();

          if (!isNil(this.$refs) && !isNil(this.$refs.picker)) {
            this.$refs.picker.hidePopover();
          }
        }
      }
    },
    handleShortcut: function (cnt, type) {
      if (this.hasFocus) {
        let tempDate = isNil(this.internalValue) ? null : this.$moment(this.internalValue, 'YYYYMMDD');

        if (isNil(tempDate)) {
          tempDate = isNil(this.minDate) || this.minDate === '18000101' ? this.$moment() : this.$moment(this.minDate, 'YYYYMMDD');
        }

        tempDate = tempDate.add(cnt, type);

        tempDate = this.validateTempDate(tempDate);

        this.internalValue = isNil(tempDate) ? null : tempDate.format('YYYYMMDD');
        this.formattedDate = isNil(tempDate) ? null : tempDate.format(this.$moment.localeData().longDateFormat('L'));
      }
    },
    recalculatePosition: function () {
      if (!isNil(this.$el)) {
        this.calendarPositionParams.offsetLeft = this.$el.offsetLeftRelativeTo(this.$el.closest('.main'));
        this.calendarPositionParams.width = this.$el.offsetWidth;
      }
    },
    isNil: function (val) {
      return isNil(val);
    },
    onFocus: function () {
      this.$emit('focus');
      this.hasFocus = true;
      this.$el.querySelector('input').select();
    },
    onBlur: function () {
      this.$emit('blur');
      this.hasFocus = false;

      // this.updateDateFromFormatted();

      /* this is required for quick entry - if date selected already is today and users enters '0' then we need to make
           sure we also update the formattedDate (displayed value) so we display todays date in the input and not the digit '0'
         */
      if (this.required) {
        if (isNil(this.internalValue) && isEmpty(this.value)) {
          this.formattedDate = this.$moment().format(this.$moment.localeData().longDateFormat('L'));
        } else if (isNil(this.internalValue) && !isEmpty(this.value)) {
          this.formattedDate = this.$moment(this.value, 'YYYYMMDD').format(this.$moment.localeData().longDateFormat('L'));
        } else {
          this.formattedDate = isNil(this.internalValue) ? this.$moment().format(this.$moment.localeData().longDateFormat('L')) : this.$moment(this.internalValue, 'YYYYMMDD').format(this.$moment.localeData().longDateFormat('L'));
        }
      } else {
        this.formattedDate = isNil(this.internalValue) ? null : this.$moment(this.internalValue, 'YYYYMMDD').format(this.$moment.localeData().longDateFormat('L'));
      }
    },
    updateDateFromFormatted() {
      // check if it does not contain any known date separators
      if (!isEmpty(this.formattedDate) && (['.', '/', '-'].filter(it => this.formattedDate.includes(it)).length === 0 || this.formattedDate.indexOf('-') === 0)) {
        const baseDate = isNil(this.minDate) || this.minDate === '18000101' ? this.$moment() : this.$moment(this.minDate, 'YYYYMMDD');
        let tempDate = baseDate.add(this.formattedDate, 'days');

        tempDate = this.validateTempDate(tempDate);

        this.internalValue = isNil(tempDate) ? null : tempDate.format('YYYYMMDD');
      } else if (!isEmpty(this.formattedDate)) {
        let tempDate = isNil(this.formattedDate) ? null : this.$moment(this.formattedDate, this.$moment.localeData().longDateFormat('L'));

        tempDate = this.validateTempDate(tempDate);

        this.internalValue = isNil(tempDate) ? null : tempDate.format('YYYYMMDD');
      }
    },
    validateTempDate(tempDate) {
      if (tempDate.isBefore(this.minimumDate)) {
        // in case someone does just enter MM.DD. and this date is already past in the current year, try to add 1 year
        if (!isNil(tempDate._pf) && !isEmpty(tempDate._pf.unusedTokens) && tempDate._pf.unusedTokens.filter(it => it.includes('Y')).length > 0) {
          tempDate = tempDate.add(1, 'year');
          return this.validateTempDate(tempDate);
        } else {
          tempDate = this.$moment(this.minimumDate);
        }
      }

      if (!isNil(this.maximumDate) && tempDate.isAfter(this.maximumDate)) {
        tempDate = this.$moment(this.maximumDate);
      }

      if (!tempDate.isValid() && this.required) {
        if (!isNil(this.internalValue)) {
          tempDate = this.$moment(this.internalValue, 'YYYYMMDD');

          // required to check against minimum and maximum date
          return this.validateTempDate(tempDate);
        } else {
          tempDate = this.$moment();

          // required to check against minimum and maximum date
          return this.validateTempDate(tempDate);
        }
      } else if (!tempDate.isValid()) {
        tempDate = null;
      }

      return tempDate;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
</style>

<style lang="scss">
  @import '~styles/import';

  .component__hm-input-date {
    .date {
      @include icon-color($middlegray);
      //@include icon-hover($text);
      @include icon-input-hover($text);
      @include icon-input-focus($text);
    }

    .hm-input__icon__wrapper {
      pointer-events: none;
      @include icon-color($middlegray);
    }

    .hm-input__icon--input-focus {
      .hm-input__icon__wrapper hm-input__icon__wrapper--right {
        @include icon-color($text);
      }
    }
  }

  .vc-day-content {
    font-weight: normal!important;
  }

  .vc-day-content.vc-font-bold.vc-text-white {
    pointer-events: none!important;
  }

  .vc-bg-teal-600 {
    background-color: $mint!important;
  }

  .vc-grid-cell:before {
    content: ' ';
    display: inline-block;
    width: 0.001px;
    height: 0;
    padding-bottom: 100%;
  }

  .vc-day-box-center-center {
    flex-direction: column!important;
    flex-grow: 1!important;
    position: relative!important;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }

  .vc-h-full {
    width: 100%!important;
  }

  .vc-day {
    position: absolute!important;
  }

  .vc-grid-cell {
    position: relative!important;
  }

  .vc-day-content {
    position: absolute;
    line-height: 1!important;
    font-size: 14px!important;
    display: block!important;
    width: auto!important;
    height: auto!important;
    top: calc(50% - 1px);
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 7px;
    min-width: 30px;
    min-height: 30px;
    text-align: center;
  }

  .vc-day-layer.vc-day-box-center-center {
    border-radius: 0!important;
  }

  .vc-highlight {
    width: 100%!important;
    height: 100%!important;
  }

  .vc-day-content:hover {
    background-color: $lightmint!important;
  }
</style>



// WEBPACK FOOTER //
// src/components/lib/inputs/HmInputDate.vue